// Every Firestore path in Clevis goes through this file.
// Never build a path string anywhere else — this is the single map of the database.

export const paths = {
  // ---------- Global ----------
  superAdmins: () => `superAdmins`,
  superAdmin: (uid: string) => `superAdmins/${uid}`,
  users: () => `users`,
  user: (uid: string) => `users/${uid}`,
  invites: () => `invites`,
  invite: (token: string) => `invites/${token}`,
  plans: () => `plans`,
  plan: (planId: string) => `plans/${planId}`,
  appConfig: (docId: string) => `appConfig/${docId}`,
  integrationLibrary: () => `integrationLibrary`,
  integrationLibraryEntry: (slug: string) => `integrationLibrary/${slug}`,
  appMessages: () => `appMessages`,
  appMessage: (id: string) => `appMessages/${id}`,
  auditLog: () => `auditLog`,
  idempotencyKeys: () => `idempotencyKeys`,
  idempotencyKey: (key: string) => `idempotencyKeys/${key}`,

  // ---------- Support ----------
  supportTickets: () => `supportTickets`,
  supportTicket: (ticketId: string) => `supportTickets/${ticketId}`,
  supportTicketMessages: (ticketId: string) => `supportTickets/${ticketId}/messages`,
  remoteSupportSessions: () => `remoteSupportSessions`,
  remoteSupportSession: (sessionId: string) => `remoteSupportSessions/${sessionId}`,
  remoteSupportSignals: (sessionId: string) => `remoteSupportSessions/${sessionId}/signals`,
  remoteSupportSignal: (sessionId: string, docId: string) =>
    `remoteSupportSessions/${sessionId}/signals/${docId}`,

  // ---------- Tenant subtree ----------
  tenants: () => `tenants`,
  tenant: (tid: string) => `tenants/${tid}`,
  tenantPublicEntitlements: (tid: string) => `tenants/${tid}/publicEntitlements/main`,
  tenantLocations: (tid: string) => `tenants/${tid}/locations`,
  tenantLocation: (tid: string, locationId: string) => `tenants/${tid}/locations/${locationId}`,
  tenantMembers: (tid: string) => `tenants/${tid}/members`,
  tenantMember: (tid: string, uid: string) => `tenants/${tid}/members/${uid}`,
  tenantCustomers: (tid: string) => `tenants/${tid}/customers`,
  tenantCustomer: (tid: string, customerId: string) => `tenants/${tid}/customers/${customerId}`,
  tenantJobs: (tid: string) => `tenants/${tid}/jobs`,
  tenantJob: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}`,
  tenantJobNotes: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/notes`,
  tenantJobActivity: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/activity`,
  tenantJobOrderedItems: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/orderedItems`,
  tenantJobSignoffs: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/signoffs`,
  tenantDepartments: (tid: string) => `tenants/${tid}/departments`,
  tenantDepartment: (tid: string, id: string) => `tenants/${tid}/departments/${id}`,
  tenantProjects: (tid: string) => `tenants/${tid}/projects`,
  tenantProject: (tid: string, id: string) => `tenants/${tid}/projects/${id}`,
  // What was done and used on a job or visit (functions write; stock moves with it).
  tenantJobWorkItems: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/workItems`,
  tenantJobWorkItem: (tid: string, jobId: string, id: string) => `tenants/${tid}/jobs/${jobId}/workItems/${id}`,
  // Customer texting (schemas/messaging.ts).
  tenantTextingSettings: (tid: string) => `tenants/${tid}/settings/texting`,
  tenantMessages: (tid: string) => `tenants/${tid}/messages`,
  tenantFeedback: (tid: string) => `tenants/${tid}/feedback`,
  tenantFeedbackItem: (tid: string, id: string) => `tenants/${tid}/feedback/${id}`,
  tenantOutbox: (tid: string) => `tenants/${tid}/outbox`,
  tenantTextClaim: (tid: string, id: string) => `tenants/${tid}/textClaims/${id}`,
  customerLink: (token: string) => `customerLinks/${token}`,
  // Last company/customer/job each phone was texted for: routes replies and STOP.
  smsRoute: (e164: string) => `smsRoutes/${e164}`,
  /** Which company's tracking number a phone number is (server-only). */
  phoneRoute: (e164: string) => `phoneRoutes/${e164}`,
  memberPhotoStorage: (tid: string, uid: string) => `tenants/${tid}/memberPhotos/${uid}.jpg`,
  // Company documents (W-9, certificates…) and the log of every send (schemas/companyDocs.ts).
  tenantCompanyDocs: (tid: string) => `tenants/${tid}/companyDocs`,
  tenantCompanyDoc: (tid: string, id: string) => `tenants/${tid}/companyDocs/${id}`,
  companyDocStorage: (tid: string, docId: string, fileName: string) => `tenants/${tid}/companyDocs/${docId}/${fileName}`,
  tenantDocSends: (tid: string) => `tenants/${tid}/docSends`,
  tenantProjectItems: (tid: string, pid: string) => `tenants/${tid}/projects/${pid}/items`,
  tenantProjectItem: (tid: string, pid: string, id: string) => `tenants/${tid}/projects/${pid}/items/${id}`,
  tenantProjectLots: (tid: string, pid: string) => `tenants/${tid}/projects/${pid}/lots`,
  tenantProjectLot: (tid: string, pid: string, id: string) => `tenants/${tid}/projects/${pid}/lots/${id}`,
  // Who sees what (owner-set): role and per-person section overrides.
  tenantAccessPolicy: (tid: string) => `tenants/${tid}/settings/access`,
  // The company's menu groups (owner-set; schemas in access.ts).
  tenantMenuLayout: (tid: string) => `tenants/${tid}/settings/menu`,
  /** Estimate types and terms, invoice terms, commission rules. */
  tenantSalesSettings: (tid: string) => `tenants/${tid}/settings/sales`,
  /** The company's customer types (Homeowner, Builder, Commercial, their own). */
  tenantCustomerSettings: (tid: string) => `tenants/${tid}/settings/customers`,
  /** The daily lien-notice check's result (dashboard). */
  tenantLienNotices: (tid: string) => `tenants/${tid}/settings/lienNotices`,
  // A person's own UI preferences: menu order, favorites, dashboard widgets.
  userUiPrefs: (uid: string) => `users/${uid}/prefs/ui`,
  // Builder cleanup runs (FieldPulse duplicates → one customer + projects); moves/{childId} hold the undo data.
  tenantCleanupRuns: (tid: string) => `tenants/${tid}/cleanupRuns`,
  tenantCleanupRun: (tid: string, runId: string) => `tenants/${tid}/cleanupRuns/${runId}`,
  tenantCleanupMoves: (tid: string, runId: string) => `tenants/${tid}/cleanupRuns/${runId}/moves`,
  tenantVendors: (tid: string) => `tenants/${tid}/vendors`,
  tenantVendor: (tid: string, id: string) => `tenants/${tid}/vendors/${id}`,
  tenantPurchaseOrders: (tid: string) => `tenants/${tid}/purchaseOrders`,
  tenantPurchaseOrder: (tid: string, id: string) => `tenants/${tid}/purchaseOrders/${id}`,
  /** Supplier order acknowledgments, as uploaded. */
  purchaseOrderAckStorage: (tid: string, poId: string, fileName: string) => `tenants/${tid}/purchaseOrders/${poId}/${fileName}`,
  tenantServiceAgreements: (tid: string) => `tenants/${tid}/serviceAgreements`,
  tenantTodos: (tid: string) => `tenants/${tid}/todos`,
  tenantTodo: (tid: string, id: string) => `tenants/${tid}/todos/${id}`,
  tenantProjectFiles: (tid: string, projectId: string) => `tenants/${tid}/projects/${projectId}/files`,
  projectFileStorage: (tid: string, projectId: string, fileName: string) => `tenants/${tid}/projectFiles/${projectId}/${fileName}`,
  /** token → { tenantId, uid, scope } for calendar subscribe links (server-only). */
  calendarToken: (token: string) => `calendarTokens/${token}`,
  tenantServiceAgreement: (tid: string, id: string) => `tenants/${tid}/serviceAgreements/${id}`,
  tenantInventoryMovements: (tid: string) => `tenants/${tid}/inventoryMovements`,
  /** Items loaded onto trucks for jobs (functions write). */
  tenantStockLoads: (tid: string) => `tenants/${tid}/stockLoads`,
  tenantStockLoad: (tid: string, id: string) => `tenants/${tid}/stockLoads/${id}`,
  tenantAvailability: (tid: string) => `tenants/${tid}/availability`,
  tenantAvailabilityDoc: (tid: string, uid: string) => `tenants/${tid}/availability/${uid}`,
  tenantTemplates: (tid: string) => `tenants/${tid}/templates`,
  tenantTemplate: (tid: string, id: string) => `tenants/${tid}/templates/${id}`,
  tenantAgreements: (tid: string) => `tenants/${tid}/agreements`,
  tenantAgreement: (tid: string, id: string) => `tenants/${tid}/agreements/${id}`,
  tenantJobPhotos: (tid: string, jobId: string) => `tenants/${tid}/jobs/${jobId}/photos`,
  jobPhotoStorage: (tid: string, jobId: string, fileName: string) =>
    `tenants/${tid}/jobPhotos/${jobId}/${fileName}`,
  tenantAuditLog: (tid: string) => `tenants/${tid}/auditLog`,
  tenantCounters: (tid: string) => `tenants/${tid}/counters/main`,
  tenantRequests: (tid: string) => `tenants/${tid}/requests`,
  tenantRequest: (tid: string, requestId: string) => `tenants/${tid}/requests/${requestId}`,
  tenantRequestFormSettings: (tid: string) => `tenants/${tid}/settings/requestForm`,
  /** The website chat that books visits (Requests → AI chat scheduling). */
  tenantAiChatSettings: (tid: string) => `tenants/${tid}/settings/aiChat`,
  /** Clevis Relay (referral rewards): the company's rules, each referral, and its Tremendous key (encrypted; no client reads it). */
  tenantReferralSettings: (tid: string) => `tenants/${tid}/settings/referrals`,
  tenantReferrals: (tid: string) => `tenants/${tid}/referrals`,
  tenantReferral: (tid: string, id: string) => `tenants/${tid}/referrals/${id}`,
  tenantReferralSecret: (tid: string) => `tenants/${tid}/referralSecrets/tremendous`,
  tenantDocumentSets: (tid: string) => `tenants/${tid}/documentSets`,
  tenantDocumentSet: (tid: string, id: string) => `tenants/${tid}/documentSets/${id}`,
  tenantReferralCampaigns: (tid: string) => `tenants/${tid}/referralCampaigns`,
  tenantReferralCampaign: (tid: string, id: string) => `tenants/${tid}/referralCampaigns/${id}`,
  /** Clevis Relay: opens of customers' links, one doc per UTC day (functions write; Reports read). */
  tenantReferralDays: (tid: string) => `tenants/${tid}/referralDays`,
  tenantReferralDay: (tid: string, day: string) => `tenants/${tid}/referralDays/${day}`,
  /** Tech-logged piece work waiting for a manager's OK (Timesheets). */
  tenantPieceReviews: (tid: string) => `tenants/${tid}/pieceReviews`,
  tenantPieceReview: (tid: string, id: string) => `tenants/${tid}/pieceReviews/${id}`,
  /** Lead-source tracking numbers (Setup → Lead sources) and the calls they took. */
  tenantTrackingNumbers: (tid: string) => `tenants/${tid}/trackingNumbers`,
  tenantTrackingNumber: (tid: string, id: string) => `tenants/${tid}/trackingNumbers/${id}`,
  tenantCalls: (tid: string) => `tenants/${tid}/calls`,
  tenantCall: (tid: string, id: string) => `tenants/${tid}/calls/${id}`,
  /** Clevis Ring: the company's agreement (member-readable), monthly minutes (office reads), monthly charges (admins read). appConfig/ring holds the rates. */
  tenantRingSettings: (tid: string) => `tenants/${tid}/settings/ring`,
  tenantRingUsage: (tid: string, month: string) => `tenants/${tid}/ringUsage/${month}`,
  tenantRingInvoices: (tid: string) => `tenants/${tid}/ringInvoices`,
  tenantRingInvoice: (tid: string, month: string) => `tenants/${tid}/ringInvoices/${month}`,
  requestRateLimits: () => `requestRateLimits`,
  requestRateLimit: (id: string) => `requestRateLimits/${id}`,
  // Customer portal: who may sign in (office reads), and the functions-only sign-in records.
  tenantPortalContacts: (tid: string) => `tenants/${tid}/portalContacts`,
  tenantPortalContact: (tid: string, id: string) => `tenants/${tid}/portalContacts/${id}`,
  portalSessions: () => `portalSessions`,
  portalSession: (tokenHash: string) => `portalSessions/${tokenHash}`,
  /** "e_<sha of email>" / "p_<sha of phone>" → the contacts that sign in with it. */
  portalIndex: (key: string) => `portalIndex/${key}`,
  portalCode: (key: string) => `portalCodes/${key}`,
  /** Notes between a customer's portal contacts and the office (office reads; functions write). */
  tenantPortalMessages: (tid: string) => `tenants/${tid}/portalMessages`,
  tenantPortalMessage: (tid: string, id: string) => `tenants/${tid}/portalMessages/${id}`,
  demoRequests: () => `demoRequests`,
  demoRequest: (id: string) => `demoRequests/${id}`,
  tenantEstimates: (tid: string) => `tenants/${tid}/estimates`,
  tenantEstimate: (tid: string, id: string) => `tenants/${tid}/estimates/${id}`,
  tenantInvoices: (tid: string) => `tenants/${tid}/invoices`,
  tenantInvoice: (tid: string, id: string) => `tenants/${tid}/invoices/${id}`,
  tenantPricebook: (tid: string) => `tenants/${tid}/pricebook`,
  tenantQbCache: (tid: string, docId: string) => `tenants/${tid}/qbCache/${docId}`,
  tenantQbdAuth: (tid: string) => `tenants/${tid}/qbd/auth`,
  tenantQbdChains: (tid: string) => `tenants/${tid}/qbdChains`,
  tenantQbdChain: (tid: string, id: string) => `tenants/${tid}/qbdChains/${id}`,
  tenantQbdJobs: (tid: string) => `tenants/${tid}/qbdJobs`,
  tenantQbdJob: (tid: string, id: string) => `tenants/${tid}/qbdJobs/${id}`,
  tenantMailboxes: (tid: string) => `tenants/${tid}/mailboxes`,
  tenantMailbox: (tid: string, id: string) => `tenants/${tid}/mailboxes/${id}`,
  tenantMailboxSecret: (tid: string, id: string) => `tenants/${tid}/mailboxSecrets/${id}`,
  tenantIntakeSettings: (tid: string) => `tenants/${tid}/settings/emailIntake`,
  tenantNotificationSettings: (tid: string) => `tenants/${tid}/settings/notifications`,
  tenantIntakeItems: (tid: string) => `tenants/${tid}/intakeItems`,
  tenantIntakeItem: (tid: string, id: string) => `tenants/${tid}/intakeItems/${id}`,
  tenantQbdCustomerCacheMeta: (tid: string) => `tenants/${tid}/qbd/customerCache`,
  tenantQbdCustomerCacheChunks: (tid: string) => `tenants/${tid}/qbd/customerCache/chunks`,
  tenantQbdCustomerCacheChunk: (tid: string, n: number) => `tenants/${tid}/qbd/customerCache/chunks/${n}`,
  // Vendor / item / account / class lists mirrored from the Desktop company file.
  tenantQbdListCacheMeta: (tid: string, kind: string) => `tenants/${tid}/qbd/${kind}Cache`,
  tenantQbdListCacheChunks: (tid: string, kind: string) => `tenants/${tid}/qbd/${kind}Cache/chunks`,
  tenantQbdListCacheChunk: (tid: string, kind: string, n: number) => `tenants/${tid}/qbd/${kind}Cache/chunks/${n}`,
  tenantExpenses: (tid: string) => `tenants/${tid}/expenses`,
  tenantExpense: (tid: string, id: string) => `tenants/${tid}/expenses/${id}`,
  expenseDocStorage: (tid: string, fileName: string) => `tenants/${tid}/expenseDocs/${fileName}`,
  tenantImportJobs: (tid: string) => `tenants/${tid}/importJobs`,
  tenantImportJob: (tid: string, id: string) => `tenants/${tid}/importJobs/${id}`,
  tenantPricebookItem: (tid: string, id: string) => `tenants/${tid}/pricebook/${id}`,
  tenantBillingConfig: (tid: string) => `tenants/${tid}/billing/config`,
  // Card payments: the company's own Stripe (Connect) account and its status (schemas/payments.ts).
  tenantCardPayments: (tid: string) => `tenants/${tid}/settings/payments`,
  // Which company a connected Stripe account belongs to (functions only).
  stripeAccount: (accountId: string) => `stripeAccounts/${accountId}`,
  tenantIntegrationConfigs: (tid: string) => `tenants/${tid}/integrationConfigs`,
  tenantIntegrationConfig: (tid: string, slug: string) => `tenants/${tid}/integrationConfigs/${slug}`,

  // ---------- Routing ----------
  tenantRoutes: (tid: string) => `tenants/${tid}/routes`,
  tenantRoute: (tid: string, id: string) => `tenants/${tid}/routes/${id}`, // id = `${date}_${techUid}`
  tenantTechLocations: (tid: string) => `tenants/${tid}/techLocations`,
  tenantTechLocation: (tid: string, uid: string) => `tenants/${tid}/techLocations/${uid}`,
  tenantRoutingSettings: (tid: string) => `tenants/${tid}/settings/routing`,

  // ---------- Workforce ----------
  tenantTimeEntries: (tid: string) => `tenants/${tid}/timeEntries`,
  tenantTimeEntry: (tid: string, entryId: string) => `tenants/${tid}/timeEntries/${entryId}`,
  tenantShifts: (tid: string) => `tenants/${tid}/shifts`,
  tenantShift: (tid: string, shiftId: string) => `tenants/${tid}/shifts/${shiftId}`,
  tenantOpenJobs: (tid: string) => `tenants/${tid}/openJobs`,
  tenantOpenJob: (tid: string, openJobId: string) => `tenants/${tid}/openJobs/${openJobId}`,
  tenantPayPeriods: (tid: string) => `tenants/${tid}/payPeriods`,
  tenantPayPeriod: (tid: string, periodId: string) => `tenants/${tid}/payPeriods/${periodId}`,
  tenantPayrollRuns: (tid: string) => `tenants/${tid}/payrollRuns`,
  tenantPayrollRun: (tid: string, runId: string) => `tenants/${tid}/payrollRuns/${runId}`,
} as const;
